/* ==========================================================================
   PACK BUILDER  --  behaviour layer
   --------------------------------------------------------------------------
   - Reads ?board=<handle> from URL and swaps SSR DOM to match if needed.
   - Tracks accessory selection per category (housse | leash | ailerons | pad | wax).
     All categories are multi-select, with a stock-capped quantity per card.
   - Computes UI-only subtotal / discount estimate / total.
   - Adds the pack to cart via POST {localeRoot}cart/add.js with a shared _pack_id property.
   - Cards intentionally show NO product description (image + name + price only).
   - INF-98 (2026-10-12): premium housse -20 EUR removed. Housses show full price,
     no premium discount row, no _pack_housse_premium property. Only fins/basecoat
     free logic remains (FINS_PACK + automatic).

   Real discount is NOT applied here. It must be configured via a Shopify
   Automatic Discount rule that matches line items carrying a _pack_id property.
   ========================================================================== */

(function () {
  'use strict';

  const root = document.querySelector('.pack-builder');
  if (!root) return;

  const sectionId = root.dataset.sectionId;
  const dataEl = document.getElementById('packBuilderData-' + sectionId);
  if (!dataEl) return;

  let DATA;
  try {
    DATA = JSON.parse(dataEl.textContent);
  } catch (err) {
    console.error('[pack-builder] invalid data blob', err);
    return;
  }

  /** @returns {string} Root path for Cart/Ajax API (e.g. `/` or `/fr/`) — required on multilingual shops. */
  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  const CATEGORIES = ['housse', 'ailerons', 'leash', 'pad', 'wax'];
  const MF_KEYS = {
    housse: 'pack_housses',
    leash: 'pack_leashes',
    ailerons: 'pack_ailerons',
    pad: 'pack_pads',
    wax: 'pack_waxes'
  };

  // Smoothy uses the same pack flow but only exposes the leash and wax
  // accessories. Their product references are intentionally reused from the
  // existing pack contract until dedicated Smoothy references are configured.
  if (DATA.smoothy_boards) {
    Object.keys(DATA.smoothy_boards).forEach(function (handle) {
      const board = DATA.smoothy_boards[handle];
      const source = DATA.boards[board.pack_source_handle];
      if (source) {
        ['pack_leashes', 'pack_waxes'].forEach(function (key) {
          board[key] = source[key] || [];
        });
      }
      DATA.boards[handle] = board;
    });
  }

  const state = {
    boardHandle: root.dataset.defaultBoard || '',
    selection: { housse: [], leash: [], ailerons: [], pad: [], wax: [] }
  };

  function filterTokens(value) {
    return String(value || '').split(',').map(function (token) {
      return token.trim();
    }).filter(Boolean);
  }

  function normaliseFilterValue(value, key) {
    let normalised = String(value || '').trim().toLocaleLowerCase();
    if (key === 'size') {
      normalised = normalised.replace(/[’′]/g, "'").replace(/[”″]/g, '"');
      if (/^\d+'\d+"?$/.test(normalised)) normalised = normalised.replace(/"$/, '');
    }
    return normalised;
  }

  function syncCustomFilter(select) {
    const control = select.closest('.pack-board-filter__control');
    if (!control) return;
    const button = control.querySelector('.pack-board-filter__button');
    if (!button) return;
    const label = select.options[select.selectedIndex] ? select.options[select.selectedIndex].textContent : '';
    const text = button.querySelector('.pack-board-filter__button-text');
    if (text) text.textContent = label;
    button.setAttribute('aria-label', (select.getAttribute('aria-label') ? select.getAttribute('aria-label') + ': ' : '') + label);
    control.querySelectorAll('.pack-board-filter__option').forEach(function (option) {
      option.setAttribute('aria-selected', option.dataset.value === select.value ? 'true' : 'false');
    });
  }

  function closeCustomFilters(except) {
    qsa('.pack-board-filter__menu:not([hidden])').forEach(function (menu) {
      if (menu !== except) {
        menu.hidden = true;
        const button = menu.parentElement.querySelector('.pack-board-filter__button');
        if (button) button.setAttribute('aria-expanded', 'false');
      }
    });
  }

  function enhanceFilterSelect(select) {
    const control = select.closest('.pack-board-filter__control');
    if (!control || control.querySelector('.pack-board-filter__button')) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pack-board-filter__button';
    button.id = select.id + '-button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-label', select.getAttribute('aria-label') || '');

    const buttonText = document.createElement('span');
    buttonText.className = 'pack-board-filter__button-text';
    button.appendChild(buttonText);

    const buttonIcon = document.createElement('span');
    buttonIcon.className = 'pack-board-filter__button-icon';
    buttonIcon.setAttribute('aria-hidden', 'true');
    button.appendChild(buttonIcon);

    const menu = document.createElement('div');
    menu.className = 'pack-board-filter__menu';
    menu.id = select.id + '-menu';
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', select.getAttribute('aria-label') || '');
    menu.hidden = true;
    button.setAttribute('aria-controls', menu.id);

    Array.from(select.options).forEach(function (option) {
      const menuOption = document.createElement('button');
      menuOption.type = 'button';
      menuOption.className = 'pack-board-filter__option';
      menuOption.setAttribute('role', 'option');
      menuOption.tabIndex = -1;
      menuOption.dataset.value = option.value;
      menuOption.textContent = option.textContent;
      menuOption.addEventListener('click', function () {
        select.value = menuOption.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        menu.hidden = true;
        button.setAttribute('aria-expanded', 'false');
        button.focus();
      });
      menu.appendChild(menuOption);
    });

    button.addEventListener('click', function () {
      const willOpen = menu.hidden;
      closeCustomFilters(willOpen ? menu : null);
      menu.hidden = !willOpen;
      button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      if (willOpen) {
        const selected = menu.querySelector('[aria-selected="true"]') || menu.querySelector('.pack-board-filter__option');
        if (selected) selected.focus();
      }
    });

    button.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        button.click();
      }
    });

    menu.addEventListener('keydown', function (event) {
      const options = Array.from(menu.querySelectorAll('.pack-board-filter__option'));
      const currentIndex = options.indexOf(document.activeElement);
      if (event.key === 'Escape') {
        event.preventDefault();
        menu.hidden = true;
        button.setAttribute('aria-expanded', 'false');
        button.focus();
      } else if (event.key === 'ArrowDown' && options.length) {
        event.preventDefault();
        options[Math.min(options.length - 1, currentIndex + 1)].focus();
      } else if (event.key === 'ArrowUp' && options.length) {
        event.preventDefault();
        options[Math.max(0, currentIndex - 1)].focus();
      } else if ((event.key === 'Home' || event.key === 'End') && options.length) {
        event.preventDefault();
        options[event.key === 'Home' ? 0 : options.length - 1].focus();
      } else if (event.key === 'Tab') {
        // Return to the trigger before native tab navigation chooses the next control.
        menu.hidden = true;
        button.setAttribute('aria-expanded', 'false');
        button.focus();
      }
    });

    control.addEventListener('focusout', function (event) {
      if (!control.contains(event.relatedTarget)) {
        menu.hidden = true;
        button.setAttribute('aria-expanded', 'false');
      }
    });

    select.classList.add('is-enhanced');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');
    const label = document.querySelector('label[for="' + select.id + '"]');
    if (label) label.setAttribute('for', button.id);
    control.appendChild(button);
    control.appendChild(menu);
    syncCustomFilter(select);
  }

  function setupBoardFilters() {
    const cards = qsa('[data-board-filter-card]');
    const selects = qsa('[data-board-filter]');
    const countEl = qs('#packBoardResultsCount');
    const emptyEl = qs('#packBoardResultsEmpty');
    if (!cards.length || !selects.length) return;

    const filterKeys = ['level', 'shape', 'size', 'construction'];
    const rangeConfigs = {
      volume: { attribute: 'boardVolume', step: 0.1, parse: function (value) {
        const match = String(value || '').replace(',', '.').match(/-?\d+(?:\.\d+)?/);
        return match ? Number(match[0]) : NaN;
      } },
      budget: { attribute: 'boardPriceCents', step: 100, parse: function (value) {
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) ? parsed : NaN;
      } }
    };
    const ranges = {};
    const unspecifiedValue = '__unspecified__';
    const unspecifiedLabel = DATA.i18n && DATA.i18n.filters_unspecified;
    filterKeys.forEach(function (key) {
      const select = qs('[data-board-filter="' + key + '"]');
      if (!select) return;
      const values = new Map();
      cards.forEach(function (card) {
        const tokens = filterTokens(card.dataset['board' + key.charAt(0).toUpperCase() + key.slice(1)]);
        if (!tokens.length) values.set(unspecifiedValue, unspecifiedLabel);
        tokens.forEach(function (token) {
          const normalised = normaliseFilterValue(token, key);
          if (normalised && !values.has(normalised)) values.set(normalised, token);
        });
      });
      Array.from(values.entries()).sort(function (a, b) { return a[1].localeCompare(b[1], 'fr', { numeric: true }); }).forEach(function (entry) {
        const option = document.createElement('option');
        option.value = entry[0];
        option.textContent = entry[1];
        select.appendChild(option);
      });
      enhanceFilterSelect(select);
    });

    function rangeDisplay(key, value) {
      if (key === 'budget') return formatMoney(value);
      return Number(value).toLocaleString(document.documentElement.lang || 'fr', { maximumFractionDigits: 1 }) + ' L';
    }

    function syncRange(key) {
      const config = rangeConfigs[key];
      const range = ranges[key];
      if (!config || !range) return;
      let lower = Number(range.lower.value);
      let upper = Number(range.upper.value);
      if (lower > upper) {
        if (range.activeInput === range.lower) {
          upper = lower;
          range.upper.value = String(upper);
        } else {
          lower = upper;
          range.lower.value = String(lower);
        }
      }
      range.lower.setAttribute('aria-valuetext', (range.lower.getAttribute('aria-label') || '') + ': ' + rangeDisplay(key, lower));
      range.upper.setAttribute('aria-valuetext', (range.upper.getAttribute('aria-label') || '') + ': ' + rangeDisplay(key, upper));
      const rangeGroup = range.lower.parentElement;
      if (rangeGroup) {
        const span = range.max - range.min || 1;
        rangeGroup.style.setProperty('--range-min', ((lower - range.min) / span) * 100 + '%');
        rangeGroup.style.setProperty('--range-max', ((upper - range.min) / span) * 100 + '%');
      }
      const valueEl = qs('[data-board-range-value="' + key + '"]');
      const allLabel = (DATA.i18n && DATA.i18n.filters_all) || '';
      const rangeLabel = (DATA.i18n && DATA.i18n.filters_range) || '{{min}} – {{max}}';
      if (valueEl) {
        valueEl.textContent = lower === range.min && upper === range.max
          ? allLabel
          : rangeLabel.replace('{{min}}', rangeDisplay(key, lower)).replace('{{max}}', rangeDisplay(key, upper));
      }
      range.lower.setAttribute('data-value', String(lower));
      range.upper.setAttribute('data-value', String(upper));
    }

    Object.keys(rangeConfigs).forEach(function (key) {
      const config = rangeConfigs[key];
      const values = cards.map(function (card) { return config.parse(card.dataset[config.attribute]); }).filter(Number.isFinite);
      const lower = qs('[data-board-range="' + key + '-min"]');
      const upper = qs('[data-board-range="' + key + '-max"]');
      if (!lower || !upper) return;
      if (!values.length) {
        const filter = lower.closest('[data-board-range-filter="' + key + '"]');
        if (filter) filter.hidden = true;
        return;
      }
      const min = Math.floor(Math.min.apply(Math, values) / config.step) * config.step;
      const max = Math.ceil(Math.max.apply(Math, values) / config.step) * config.step;
      [lower, upper].forEach(function (input) {
        input.min = String(min);
        input.max = String(max);
        input.step = String(config.step);
        input.disabled = false;
      });
      lower.value = String(min);
      upper.value = String(max);
      ranges[key] = { min: min, max: max, lower: lower, upper: upper, activeInput: null };
      [lower, upper].forEach(function (input) {
        input.addEventListener('input', function () {
          ranges[key].activeInput = input;
          syncRange(key);
          applyFilters();
        });
        input.addEventListener('change', function () {
          ranges[key].activeInput = input;
          syncRange(key);
          applyFilters();
        });
      });
      const minBound = qs('[data-board-range-bound="' + key + '-min"]');
      const maxBound = qs('[data-board-range-bound="' + key + '-max"]');
      if (minBound) minBound.textContent = rangeDisplay(key, min);
      if (maxBound) maxBound.textContent = rangeDisplay(key, max);
      syncRange(key);
    });

    function applyFilters() {
      const active = {};
      filterKeys.forEach(function (key) {
        const select = qs('[data-board-filter="' + key + '"]');
        active[key] = select ? select.value : '';
      });
      let visibleCount = 0;
      cards.forEach(function (card) {
        const matches = filterKeys.every(function (key) {
          if (!active[key]) return true;
          const attr = 'board' + key.charAt(0).toUpperCase() + key.slice(1);
          const values = filterTokens(card.dataset[attr]).map(function (value) { return normaliseFilterValue(value, key); });
          return values.indexOf(active[key]) !== -1 || (!values.length && active[key] === unspecifiedValue);
        }) && Object.keys(ranges).every(function (key) {
          const range = ranges[key];
          const value = rangeConfigs[key].parse(card.dataset[rangeConfigs[key].attribute]);
          const isActive = Number(range.lower.value) !== range.min || Number(range.upper.value) !== range.max;
          return !isActive || (Number.isFinite(value) && value >= Number(range.lower.value) && value <= Number(range.upper.value));
        });
        card.hidden = !matches;
        if (matches) visibleCount += 1;
      });
      selects.forEach(syncCustomFilter);
      if (countEl) countEl.textContent = String(visibleCount);
      if (emptyEl) emptyEl.hidden = visibleCount > 0;
    }

    selects.forEach(function (select) { select.addEventListener('change', applyFilters); });
    document.addEventListener('click', function (event) {
      if (!event.target.closest('.pack-board-filter__control')) closeCustomFilters();
    });
    const reset = qs('#packBoardFiltersReset');
    if (reset) {
      reset.addEventListener('click', function () {
        selects.forEach(function (select) { select.value = ''; });
        Object.keys(ranges).forEach(function (key) {
          const range = ranges[key];
          range.lower.value = String(range.min);
          range.upper.value = String(range.max);
          syncRange(key);
        });
        applyFilters();
      });
    }
    applyFilters();
  }

  function setupStepNavigation() {
    const nav = qs('[data-pack-step-nav]');
    if (!nav) return;
    const links = qsa('[data-pack-step-link]', nav);
    const sections = links.map(function (link) {
      return document.getElementById(link.getAttribute('aria-controls'));
    }).filter(Boolean);
    const count = qs('[data-pack-step-count]', nav);
    if (!links.length || !sections.length) return;

    let activeIndex = -1;
    function setActive(index) {
      const nextIndex = Math.max(0, Math.min(index, sections.length - 1));
      if (nextIndex === activeIndex) return;
      activeIndex = nextIndex;
      links.forEach(function (link, linkIndex) {
        const isActive = linkIndex === activeIndex;
        link.classList.toggle('is-active', isActive);
        link.classList.toggle('is-complete', linkIndex < activeIndex);
        if (isActive) link.setAttribute('aria-current', 'step');
        else link.removeAttribute('aria-current');
      });
      if (count) count.textContent = (activeIndex + 1) + '/' + sections.length;
    }

    function updateFromScroll() {
      const navHeight = nav.getBoundingClientRect().height;
      // Advance the story when the next section reaches the first readable
      // content zone below the sticky navigator, before its title is covered.
      const marker = window.scrollY + (navHeight * 2) + 16;
      let nextIndex = 0;
      sections.forEach(function (section, sectionIndex) {
        if (section.getBoundingClientRect().top + window.scrollY <= marker) nextIndex = sectionIndex;
      });
      setActive(nextIndex);
    }

    links.forEach(function (link, index) {
      link.addEventListener('click', function (event) {
        const section = sections[index];
        if (!section) return;
        event.preventDefault();
        setActive(index);
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.replaceState(null, '', '#' + section.id);
      });
    });

    window.addEventListener('scroll', updateFromScroll, { passive: true });
    window.addEventListener('resize', updateFromScroll);
    updateFromScroll();
  }

  function offeredLabel() {
    return DATA.i18n && DATA.i18n.offered;
  }

  // ------------------------------------------------------------------
  // URL handling
  // ------------------------------------------------------------------
  function getUrlBoard() {
    const params = new URLSearchParams(window.location.search);
    return (params.get('board') || '').trim();
  }

  // Product handles are localized (FR "planche-de-surf-…", EN "surfboard-…" /
  // "wooden-surfboard-…", ES "tabla-de-surf-…"). A deep link shared from
  // another language must still resolve, so compare the locale-independent
  // core of the handle once the locale prefix is stripped.
  function stripBoardHandlePrefix(handle) {
    const value = (handle || '').trim().toLowerCase();
    const prefixes = [
      'planche-de-surf-bois-',
      'planche-de-surf-',
      'wooden-surfboard-',
      'surfboard-',
      'tabla-de-surf-madera-',
      'tabla-de-surf-'
    ];
    for (let i = 0; i < prefixes.length; i += 1) {
      if (value.indexOf(prefixes[i]) === 0) {
        return value.slice(prefixes[i].length);
      }
    }
    return value;
  }

  function resolveUrlBoard(handle) {
    if (!handle) return handle;
    if (DATA.boards && DATA.boards[handle]) return handle;
    const core = stripBoardHandlePrefix(handle);
    if (core) {
      const keys = Object.keys(DATA.boards || {});
      for (let i = 0; i < keys.length; i += 1) {
        if (stripBoardHandlePrefix(keys[i]) === core) {
          return keys[i];
        }
      }
    }
    return handle;
  }

  // ------------------------------------------------------------------
  // Money formatting  --  basic replacement for {{ price | money }}
  // Supports {{amount}} / {{amount_no_decimals}} / {{amount_with_comma_separator}}
  // ------------------------------------------------------------------
  function formatMoney(cents) {
    const format = DATA.money_format || '${{amount}}';
    const n = Number(cents);
    if (Number.isNaN(n)) return format;
    const amount = (n / 100).toFixed(2);
    const amountNoDec = Math.round(n / 100).toString();
    const amountComma = (n / 100).toFixed(2).replace('.', ',');
    const amountCommaNoDec = Math.round(n / 100).toString();
    return format
      .replace('{{amount_no_decimals_with_comma_separator}}', amountCommaNoDec)
      .replace('{{amount_no_decimals}}', amountNoDec)
      .replace('{{amount_with_comma_separator}}', amountComma)
      .replace('{{amount}}', amount);
  }

  // ------------------------------------------------------------------
  // DOM helpers
  // ------------------------------------------------------------------
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function clearChildren(el) {
    while (el.firstChild) el.removeChild(el.firstChild);
  }

  function buildCard(item, category, badgeLabel, isSelected) {
    if (!item || item.available === false || !item.variant_id) return null;
    const card = document.createElement('div');
    card.setAttribute('role', 'group');
    card.setAttribute('aria-label', item.title);
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'accessory-card__toggle';
    card.appendChild(toggle);
    card.className = 'accessory-card' + (isSelected ? ' selected' : '');
    card.dataset.category = category;
    card.dataset.variantId = item.variant_id;
    card.dataset.price = item.price_cents;
    card.dataset.handle = item.handle;
    card.dataset.name = item.title;
    card.dataset.isPremium = item.is_premium ? 'true' : 'false';
    card.dataset.qty = '1';
    card.dataset.maxQty = String(maxStockFor(item));
    toggle.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

    const check = document.createElement('span');
    check.className = 'accessory-card__check';
    check.setAttribute('aria-hidden', 'true');
    toggle.appendChild(check);

    const imgWrap = document.createElement('div');
    imgWrap.className = 'accessory-card__image';
    if (item.image_url) {
      const img = document.createElement('img');
      img.src = item.image_url;
      img.alt = item.title;
      img.loading = 'lazy';
      imgWrap.appendChild(img);
    }
    toggle.appendChild(imgWrap);

    if (badgeLabel) {
      const badge = document.createElement('span');
      badge.className = 'accessory-card__badge';
      badge.textContent = badgeLabel;
      toggle.appendChild(badge);
    }

    const name = document.createElement('span');
    name.className = 'accessory-card__name';
    name.textContent = item.title;
    toggle.appendChild(name);

    // No product description on purpose: image + name + price only.

    if (category === 'pad' && Array.isArray(item.variants) && item.variants.length > 1) {
      const group = document.createElement('div');
      group.className = 'pad-variant-swatches';
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', 'Couleur');
      item.variants.filter(function (v) { return v && v.available !== false; }).forEach(function (v, idx) {
        const key = (v.title || '').toLowerCase();
        let color = '#cccccc';
        if (key.indexOf('black') !== -1) color = '#000000';
        else if (key.indexOf('dark blue') !== -1 || key.indexOf('marine') !== -1) color = '#0f3057';
        else if (key.indexOf('orange') !== -1) color = '#ff8c42';
        else if (key.indexOf('pink') !== -1) color = '#ff6fa3';
        else if (key.indexOf('turquoise') !== -1 || key.indexOf('lagoon') !== -1) color = '#4ecdc4';
        else if (key.indexOf('yellow') !== -1) color = '#ffd93d';
        else if (key.indexOf('purple') !== -1) color = '#8b5cf6';
        const sw = document.createElement('button');
        sw.type = 'button';
        const active = String(v.id) === String(item.variant_id);
        sw.className = 'pad-variant-swatch' + (active ? ' is-active' : '');
        sw.dataset.variantId = v.id;
        sw.dataset.price = v.price_cents;
        sw.dataset.title = v.title;
        if (v.image_url) sw.dataset.image = v.image_url;
        sw.setAttribute('aria-pressed', active ? 'true' : 'false');
        sw.style.backgroundColor = color;
        sw.title = v.title;
        const hidden = document.createElement('span');
        hidden.className = 'visually-hidden';
        hidden.textContent = v.title;
        sw.appendChild(hidden);
        group.appendChild(sw);
      });
      card.appendChild(group);
    }

    const price = document.createElement('span');
    const unitCents = parseInt(item.price_cents, 10) || 0;
    price.className = 'accessory-card__price';
    price.textContent = item.price_formatted || formatMoney(unitCents);
    toggle.appendChild(price);

    {
      const qty = document.createElement('span');
      qty.className = 'accessory-qty' + (isSelected ? '' : ' accessory-qty--hidden');
      qty.setAttribute('data-accessory-qty', '');
      const dec = document.createElement('button');
      dec.type = 'button';
      dec.className = 'accessory-qty-btn';
      dec.setAttribute('data-accessory-dec', '');
      dec.setAttribute('aria-label', (DATA.i18n && DATA.i18n.quantity_decrease) || 'Decrease quantity');
      dec.textContent = '\u2212';
      const val = document.createElement('span');
      val.className = 'accessory-qty-val';
      val.setAttribute('data-accessory-val', '');
      val.textContent = '1';
      const inc = document.createElement('button');
      inc.type = 'button';
      inc.className = 'accessory-qty-btn';
      inc.setAttribute('data-accessory-inc', '');
      inc.setAttribute('aria-label', (DATA.i18n && DATA.i18n.quantity_increase) || 'Increase quantity');
      inc.textContent = '+';
      qty.appendChild(dec);
      qty.appendChild(val);
      qty.appendChild(inc);
      card.appendChild(qty);
    }

    return card;
  }

  // ------------------------------------------------------------------
  // Board size variants (surfboard length) — sidebar chips, pattern similar to pad swatches
  // ------------------------------------------------------------------
  function mountBoardVariantPicker(board) {
    const slot = document.getElementById('packBoardVariantSlot');
    if (!slot) return;
    clearChildren(slot);
    const vars = board.variants;
    if (!vars || vars.length < 2) {
      slot.hidden = true;
      return;
    }
    slot.hidden = false;
    const lbl = document.createElement('span');
    lbl.className = 'pack-board-variant__label';
    lbl.textContent = (DATA.i18n && DATA.i18n.board_size_label) || '';

    const group = document.createElement('div');
    group.className = 'board-variant-chips';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', lbl.textContent || 'Size');

    const activeId = board.variant_id;
    vars.forEach(function (v) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'board-variant-chip' + (v.id === activeId ? ' is-active' : '');
      btn.dataset.variantId = String(v.id);
      btn.dataset.priceCents = String(v.price_cents);
      btn.dataset.imageUrl = v.image_url || '';
      btn.textContent = v.label || v.title;
      btn.disabled = !v.available;
      btn.setAttribute('aria-pressed', v.id === activeId ? 'true' : 'false');
      group.appendChild(btn);
    });

    slot.appendChild(lbl);
    slot.appendChild(group);
  }

  function syncBoardCategories(board) {
    const allowed = Array.isArray(board.allowed_categories) ? board.allowed_categories : CATEGORIES;
    CATEGORIES.forEach(function (cat) {
      const section = qs('.pack-category[data-category="' + cat + '"]');
      if (section) section.hidden = allowed.indexOf(cat) === -1;
      const summary = qs('[data-selection-category="' + cat + '"]');
      if (summary) summary.hidden = allowed.indexOf(cat) === -1;
      if (allowed.indexOf(cat) === -1) {
        state.selection[cat] = [];
      }
    });
  }

  // ------------------------------------------------------------------
  // Board swap  --  rebuilds sidebar + grids from JSON when URL board
  // differs from the SSR default (or when SSR had no default).
  // ------------------------------------------------------------------
  function swapToBoard(handle) {
    const board = DATA.boards[handle];
    if (!board) {
      console.warn('[pack-builder] unknown board handle', handle);
      return false;
    }

    state.boardHandle = handle;
    syncBoardCategories(board);

    const sb = qs('#packSidebar');
    if (sb) {
      sb.dataset.boardPrice = board.price_cents;
      sb.dataset.boardVariantId = board.variant_id;
    }

    const imgEl = qs('.pack-selected-board__image');
    if (imgEl) {
      clearChildren(imgEl);
      if (board.image_url) {
        const img = document.createElement('img');
        img.src = board.image_url;
        img.alt = board.title;
        imgEl.appendChild(img);
      }
    }

    const shapeEl = qs('.pack-selected-board__shape');
    if (shapeEl) shapeEl.textContent = board.shape || '';

    const nameEl = qs('.pack-selected-board__name');
    if (nameEl) nameEl.textContent = board.title;

    const specsEl = qs('.pack-selected-board__specs');
    if (specsEl) {
      const parts = [];
      if (board.volume) {
        var volClean = String(board.volume).replace(/l/gi, '').replace(',', '.').trim();
        var volNumber = Number(volClean);
        if (volClean) {
          var volFormatted = Number.isFinite(volNumber)
            ? volNumber.toLocaleString(document.documentElement.lang || 'fr', { maximumFractionDigits: 1 })
            : volClean;
          parts.push(volFormatted + ' L');
        }
      }
      if (board.construction) parts.push(board.construction);
      specsEl.textContent = parts.join(' · ');
    }

    const boardPriceEl = qs('.pack-selected-board__price');
    if (boardPriceEl) boardPriceEl.textContent = board.price_formatted || formatMoney(board.price_cents);

    // Update category descriptions with new board name
    CATEGORIES.forEach(function (cat) {
      if (cat === 'wax') return;
      const descEl = qs('[data-category-desc="' + cat + '"]');
      if (!descEl) return;
      const tpl = DATA.i18n['cat_' + cat + '_desc'] || '';
      descEl.textContent = tpl.replace('__BOARD__', board.title);
    });

    // Re-render each category grid
    CATEGORIES.forEach(function (cat) {
      const grid = qs('[data-grid="' + cat + '"]');
      if (!grid) return;
      const items = board[MF_KEYS[cat]] || [];
      const badge = (DATA.badges && DATA.badges[cat]) || '';

      qsa('.accessory-card:not(.accessory-card--skip)', grid).forEach(function (el) { el.remove(); });

      const skipCard = qs('.accessory-card--skip', grid);
      items.forEach(function (item, idx) {
        const card = buildCard(item, cat, idx === 0 ? badge : '', idx === 0);
        if (!card) return;
        if (skipCard) grid.insertBefore(card, skipCard);
        else grid.appendChild(card);
      });

      if (skipCard) {
        skipCard.setAttribute('aria-pressed', 'false');
        skipCard.classList.remove('selected');
      }
    });

    mountBoardVariantPicker(board);

    CATEGORIES.forEach(function (cat) { readInitialSelection(cat); });
    updateSidebar();
    syncHeadings();
    return true;
  }

  // ------------------------------------------------------------------
  // Stock helpers -- DATA items carry inventory_quantity (number|null) and
  // inventory_policy ('deny'|'continue'|null). Null quantity or 'continue'
  // means sellable without a cap; otherwise cap interactive quantities.
  // ------------------------------------------------------------------
  var HARD_QTY_CAP = 10;

  function maxStockFor(item) {
    if (!item) return HARD_QTY_CAP;
    if (item.inventory_management === null || item.inventory_management === '' || item.inventory_policy === 'continue') return HARD_QTY_CAP;
    var q = parseInt(item.inventory_quantity, 10);
    if (Number.isNaN(q) || q < 0) return HARD_QTY_CAP;
    return Math.max(1, Math.min(HARD_QTY_CAP, q));
  }

  function cardMaxQty(card) {
    var m = parseInt(card.dataset.maxQty, 10);
    if (Number.isNaN(m) || m < 1) return HARD_QTY_CAP;
    return Math.min(HARD_QTY_CAP, m);
  }

  // ------------------------------------------------------------------
  // Selection state (multi-select with per-card quantity in every category)
  // ------------------------------------------------------------------
  function cardQty(card) {
    const max = cardMaxQty(card);
    const q = parseInt(card.dataset.qty, 10);
    return Number.isNaN(q) || q < 1 ? 1 : Math.min(q, max);
  }

  function setCardQty(card, qty) {
    const max = cardMaxQty(card);
    const clean = Math.max(1, Math.min(max, qty || 1));
    card.dataset.qty = String(clean);
    const val = card.querySelector('[data-accessory-val]');
    if (val) val.textContent = String(clean);
  }

  function showQtyStepper(card, show) {
    const stepper = card.querySelector('[data-accessory-qty]');
    if (stepper) stepper.classList.toggle('accessory-qty--hidden', !show);
  }

  function setCardPressed(card, selected) {
    const button = card.querySelector('.accessory-card__toggle') || card;
    button.setAttribute('aria-pressed', selected ? 'true' : 'false');
  }

  function readAccessoryCard(card) {
    return {
      variantId: card.dataset.variantId,
      priceCents: parseInt(card.dataset.price, 10) || 0,
      name: card.dataset.name || '',
      handle: card.dataset.handle || '',
      qty: cardQty(card)
    };
  }

  function readInitialSelection(category) {
    const grid = qs('[data-grid="' + category + '"]');
    if (grid && grid.closest('.pack-category') && grid.closest('.pack-category').hidden) {
      state.selection[category] = [];
      return;
    }
    const selected = grid ? qsa('.accessory-card.selected:not(.accessory-card--skip)', grid)
      .filter(function (card) { return !!card.dataset.variantId; }) : [];
    state.selection[category] = selected.map(readAccessoryCard);
    const skip = grid && qs('.accessory-card--skip', grid);
    if (skip) {
      skip.classList.toggle('selected', selected.length === 0);
      skip.setAttribute('aria-pressed', selected.length === 0 ? 'true' : 'false');
    }
  }

  function selectCard(card) {
    const category = card.dataset.category;
    if (CATEGORIES.indexOf(category) === -1) return;
    const grid = qs('[data-grid="' + category + '"]');
    if (!grid) return;
    if (card.classList.contains('accessory-card--skip')) {
      qsa('.accessory-card:not(.accessory-card--skip)', grid).forEach(function (other) {
        other.classList.remove('selected');
        setCardPressed(other, false);
        setCardQty(other, 1);
        showQtyStepper(other, false);
      });
    } else {
      const selected = !card.classList.contains('selected');
      card.classList.toggle('selected', selected);
      setCardPressed(card, selected);
      if (!selected) setCardQty(card, 1);
      showQtyStepper(card, selected);
    }
    readInitialSelection(category);
    updateSidebar();
  }

  // ------------------------------------------------------------------
  // Selection thumbnails -- recap miniatures resolved from the DATA blob.
  // Images are looked up live (board swap / pad variant safe), never stored
  // in state, so a stale URL can never persist after a board change.
  // ------------------------------------------------------------------
  function accessoryImageFor(cat, sel) {
    if (!sel) return '';
    const board = DATA.boards[state.boardHandle];
    if (!board) return '';
    const list = board[MF_KEYS[cat]] || [];
    if (cat === 'pad') {
      for (let i = 0; i < list.length; i++) {
        const item = list[i];
        const inner = (item.variants || []).find(function (v) {
          return String(v.id) === String(sel.variantId);
        });
        if (inner) return inner.image_url || item.image_url || '';
        if (String(item.variant_id) === String(sel.variantId)) return item.image_url || '';
      }
      return '';
    }
    const found = list.find(function (candidate) {
      return String(candidate.variant_id) === String(sel.variantId);
    });
    return found ? (found.image_url || '') : '';
  }

  function setThumb(thumbId, images) {
    const thumb = document.getElementById(thumbId);
    if (!thumb) return;
    clearChildren(thumb);
    const list = (images || []).filter(function (img) { return img && img.src; });
    if (list.length === 0) {
      thumb.hidden = true;
      return;
    }
    thumb.hidden = false;
    list.slice(0, 3).forEach(function (img) {
      const el = document.createElement('img');
      el.src = img.src;
      el.alt = img.alt || '';
      el.loading = 'lazy';
      thumb.appendChild(el);
    });
    if (list.length > 3) {
      const more = document.createElement('span');
      more.className = 'pack-selection-item__thumb-more';
      more.textContent = '+' + String(list.length - 3);
      thumb.appendChild(more);
    }
  }

  function updateSelectionThumbs(basecoat) {
    CATEGORIES.forEach(function (cat) {
      setThumb('pack-selection-thumb-' + cat, state.selection[cat].map(function (sel) {
        return { src: accessoryImageFor(cat, sel), alt: sel.name };
      }));
    });
    if (basecoat) {
      setThumb('pack-selection-thumb-basecoat', [{ src: basecoat.image_url || '', alt: basecoat.title || '' }]);
    } else {
      setThumb('pack-selection-thumb-basecoat', []);
    }
  }

  // ------------------------------------------------------------------
  // Sidebar + pricing update
  // ------------------------------------------------------------------
  function updateSidebar() {
    const sidebar = qs('#packSidebar');
    if (!sidebar) return;
    clearPackError();

    CATEGORIES.forEach(function (cat) {
      const nameEl = document.getElementById('pack-selection-name-' + cat);
      const priceEl = document.getElementById('pack-selection-price-' + cat);
      if (!nameEl || !priceEl) return;
      const selections = state.selection[cat];
      nameEl.textContent = selections.length ? selections.map(function (sel) {
        return sel.qty > 1 ? sel.name + ' \u00d7' + sel.qty : sel.name;
      }).join(' + ') : DATA.i18n.not_selected;
      priceEl.textContent = selections.length ? formatMoney(selections.reduce(function (sum, sel) {
        return sum + sel.priceCents * sel.qty;
      }, 0)) : '-';
      nameEl.classList.toggle('pack-selection-item__name--empty', !selections.length);
      priceEl.classList.toggle('pack-selection-item__price--empty', !selections.length);
    });

    const boardPrice = parseInt(sidebar.dataset.boardPrice, 10) || 0;
    const basecoat = DATA.basecoat_wax || null;
    const basecoatPriceCents = basecoat ? (parseInt(basecoat.price_cents, 10) || 0) : 0;
    const accessoryCents = CATEGORIES.reduce(function (sum, cat) {
      return sum + state.selection[cat].reduce(function (total, sel) {
        return total + sel.priceCents * sel.qty;
      }, 0);
    }, 0);
    const selectedCount = CATEGORIES.reduce(function (sum, cat) {
      return sum + state.selection[cat].reduce(function (total, sel) { return total + sel.qty; }, 0);
    }, 0);
    const board = DATA.boards[state.boardHandle];
    const boardAvailable = !!(board && (board.variants || []).some(function (v) {
      return v.available && String(v.id) === String(sidebar.dataset.boardVariantId);
    }));

    // Basecoat is always included in the pack -- added to subtotal, then discounted 100% back out.
    const subtotal = boardPrice + accessoryCents + basecoatPriceCents;

    // Basecoat is 100% off -- shown as separate discount line.
    const basecoatDiscount = basecoat ? basecoatPriceCents : 0;

    // Ailerons are 100% off via the FINS_PACK discount code (same rule as PDP Free Fins Offer).
    // FINS_PACK covers two units in total: basecoat + the cheapest fin set.
    // Additional sets/quantities remain in the subtotal at their full price.
    const aileronsSel = state.selection.ailerons;
    const aileronsDiscount = aileronsSel.length ? Math.min.apply(null, aileronsSel.map(function (sel) {
      return sel.priceCents;
    })) : 0;

    const total = subtotal - basecoatDiscount - aileronsDiscount;

    const subEl = document.getElementById('packSubtotal');
    const totEl = document.getElementById('packTotal');

    if (subEl) subEl.textContent = formatMoney(subtotal);
    if (totEl) totEl.textContent = formatMoney(total);

    // Basecoat free row (always visible when basecoat is configured)
    const discBasecoatEl = document.getElementById('packDiscountBasecoat');
    const discBasecoatRow = document.getElementById('packDiscountRowBasecoat');
    if (basecoat && discBasecoatEl && discBasecoatRow) {
      discBasecoatEl.textContent = '-' + formatMoney(basecoatDiscount);
      discBasecoatRow.hidden = false;
    } else if (discBasecoatRow) {
      discBasecoatRow.hidden = true;
    }

    // Ailerons free row -- shown whenever a fin product (not skip) is selected.
    const discAileronsEl = document.getElementById('packDiscountAilerons');
    const discAileronsRow = document.getElementById('packDiscountRowAilerons');
    if (aileronsSel.length && discAileronsEl && discAileronsRow) {
      discAileronsEl.textContent = '-' + formatMoney(aileronsDiscount);
      discAileronsRow.hidden = false;
    } else if (discAileronsRow) {
      discAileronsRow.hidden = true;
    }

    // Basecoat line in the selection summary (always shown when basecoat is configured)
    const basecoatLineEl = document.getElementById('pack-selection-basecoat');
    const basecoatNameEl = document.getElementById('pack-selection-name-basecoat');
    if (basecoat && basecoatLineEl && basecoatNameEl) {
      basecoatNameEl.textContent = basecoat.title;
      basecoatLineEl.hidden = false;
    } else if (basecoatLineEl) {
      basecoatLineEl.hidden = true;
    }

    updateSelectionThumbs(basecoat);

    const cta = document.getElementById('packAddToCart');
    const ctaLabel = document.getElementById('packCtaLabel');
    if (cta && ctaLabel) {
      if (selectedCount === 0 || !boardAvailable) {
        cta.disabled = true;
        ctaLabel.textContent = cta.dataset.labelSelect || 'Select accessories';
      } else {
        cta.disabled = false;
        ctaLabel.textContent = cta.dataset.labelAdd || 'Add pack to cart';
      }
    }

    // Mirror state on mobile mini-CTA in toggle bar
    const miniCta = document.getElementById('packToggleCta');
    const miniCtaLabel = document.getElementById('packToggleCtaLabel');
    if (miniCta && miniCtaLabel) {
      if (selectedCount === 0 || !boardAvailable) {
        miniCta.disabled = true;
        miniCtaLabel.textContent = miniCta.dataset.labelSelect || 'Select';
      } else {
        miniCta.disabled = false;
        miniCtaLabel.textContent = miniCta.dataset.labelAdd || 'Add';
      }
    }

    const toggleCount = document.getElementById('packToggleCount');
    const togglePrice = document.getElementById('packTogglePrice');
    if (toggleCount) {
      let countText;
      if (selectedCount === 0) {
        countText = DATA.i18n.item_count_zero || '';
      } else if (selectedCount === 1) {
        countText = DATA.i18n.item_count_one || '';
      } else {
        countText = (DATA.i18n.item_count_other || '').replace('{{ count }}', String(selectedCount + 1));
      }
      toggleCount.textContent = countText;
    }
    if (togglePrice) togglePrice.textContent = formatMoney(total);
  }

  // ------------------------------------------------------------------
  // Error region + CTA reset  (never leave the button stuck on "Adding…")
  // ------------------------------------------------------------------
  function packErrorEls() {
    return {
      msg: document.getElementById('packError'),
      cartLink: document.getElementById('packErrorCartLink')
    };
  }

  function clearPackError() {
    const els = packErrorEls();
    if (els.msg) {
      els.msg.textContent = '';
      els.msg.hidden = true;
    }
    if (els.cartLink) els.cartLink.hidden = true;
  }

  function showPackError(message, withCartLink) {
    const els = packErrorEls();
    if (els.msg) {
      els.msg.textContent = message;
      els.msg.hidden = false;
    }
    if (els.cartLink) els.cartLink.hidden = !withCartLink;
  }

  function resetCtaToError(message, withCartLink) {
    updateSidebar();
    const cta = document.getElementById('packAddToCart');
    const ctaLabel = document.getElementById('packCtaLabel');
    const miniCta = document.getElementById('packToggleCta');
    const miniCtaLabel = document.getElementById('packToggleCtaLabel');
    if (cta) cta.disabled = false;
    if (ctaLabel) ctaLabel.textContent = (cta && cta.dataset.labelError) || 'Error';
    if (miniCta) miniCta.disabled = false;
    if (miniCtaLabel && miniCta) miniCtaLabel.textContent = (miniCta && miniCta.dataset.labelError) || 'Error';
    showPackError(message, withCartLink);
  }

  function errText(key, fallback) {
    return (DATA.i18n && DATA.i18n[key]) || fallback;
  }

  // ------------------------------------------------------------------
  // Headings -- exactly one h1 (the visible step), the hidden step demotes
  // its title to h2 so checkers/AT never see two h1s.
  // ------------------------------------------------------------------
  function setHeadingLevel(el, level) {
    if (!el || el.tagName === level) return el;
    const swap = document.createElement(level);
    Array.from(el.attributes).forEach(function (a) { swap.setAttribute(a.name, a.value); });
    while (el.firstChild) swap.appendChild(el.firstChild);
    el.replaceWith(swap);
    return swap;
  }

  function syncHeadings() {
    const step0 = document.getElementById('packStep0');
    const step1 = document.getElementById('packStep1');
    if (!step0 || !step1) return;
    const showStep1 = !step1.hidden;
    setHeadingLevel(document.getElementById('packStep0Title'), showStep1 ? 'H2' : 'H1');
    setHeadingLevel(document.getElementById('packStep1Title'), showStep1 ? 'H1' : 'H2');
  }

  // ------------------------------------------------------------------
  // Cart submit
  // ------------------------------------------------------------------
  function addPackToCart() {
    const cta = document.getElementById('packAddToCart');
    const ctaLabel = document.getElementById('packCtaLabel');
    if (!cta || cta.disabled) return;
    clearPackError();

    const sidebar = qs('#packSidebar');
    const boardVariantId = sidebar ? sidebar.dataset.boardVariantId : '';
    const board = DATA.boards[state.boardHandle];
    const boardVariant = board && (board.variants || []).find(function (v) {
      return String(v.id) === String(boardVariantId);
    });
    if (!boardVariant || !boardVariant.available) {
      console.error('[pack-builder] refusing unavailable board variant');
      resetCtaToError(errText('err_invalid', (cta.dataset && cta.dataset.labelError) || 'Unavailable'), false);
      return;
    }

    const packId = 'pack_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
    const items = [];
    if (boardVariantId) {
      items.push({
        id: parseInt(boardVariantId, 10),
        quantity: 1,
        properties: { '_pack_id': packId, '_pack_role': 'board' }
      });
    }

    let invalidSelection = false;
    let overStock = null;
    CATEGORIES.forEach(function (cat) {
      state.selection[cat].forEach(function (sel) {
        const product = (board && board[MF_KEYS[cat]] || []).find(function (candidate) {
          return candidate.handle === sel.handle && candidate.available === true;
        });
        const variant = product && (cat === 'pad'
          ? (product.variants || []).find(function (v) { return v.available && String(v.id) === String(sel.variantId); })
          : String(product.variant_id) === String(sel.variantId) && product);
        if (!variant) { invalidSelection = true; return; }
        if (maxStockFor(variant) < sel.qty) overStock = sel.name || product.title;
        items.push({ id: parseInt(sel.variantId, 10), quantity: sel.qty,
          properties: { '_pack_id': packId, '_pack_role': cat } });
      });
    });
    if (invalidSelection) {
      resetCtaToError(errText('err_invalid', (cta.dataset && cta.dataset.labelError) || 'Error'), false);
      return;
    }
    if (overStock) {
      resetCtaToError(errText('err_stock', '').replace('{{ product }}', overStock) || ((cta.dataset && cta.dataset.labelError) || 'Error'), false);
      return;
    }
    const basecoat = DATA.basecoat_wax;
    if (basecoat && basecoat.variant_id && basecoat.available === true) {
      items.push({
        id: parseInt(basecoat.variant_id, 10),
        quantity: 1,
        properties: { '_pack_id': packId, '_pack_role': 'basecoat' }
      });
    }

    if (items.length === 0) return;

    cta.disabled = true;
    if (ctaLabel) ctaLabel.textContent = cta.dataset.labelAdding || 'Adding...';

    const miniCta = document.getElementById('packToggleCta');
    const miniCtaLabel = document.getElementById('packToggleCtaLabel');
    if (miniCta) miniCta.disabled = true;
    if (miniCtaLabel && miniCta) miniCtaLabel.textContent = miniCta.dataset.labelAdding || 'Adding...';

    // No double packs: at most one pack per cart. A pre-submit cart read
    // refuses the POST when lines already carry a _pack_id (fail-open on
    // read error so a network blip never blocks a purchase).
    const rootPath = routesRoot();
    fetch(rootPath + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .catch(function () { return null; })
      .then(function (cart) {
        const hasPack = !!(cart && (cart.items || []).some(function (it) {
          return it.properties && it.properties._pack_id;
        }));
        if (hasPack) {
          console.warn('[pack-builder] pack already in cart, refusing double');
          resetCtaToError(errText('err_double_pack', (cta.dataset && cta.dataset.labelError) || 'Error'), true);
          return;
        }
        postPackItems(rootPath, items);
      });
  }

  function postPackItems(rootPath, items) {
    const cta = document.getElementById('packAddToCart');
    const ctaLabel = document.getElementById('packCtaLabel');
    const miniCta = document.getElementById('packToggleCta');
    const miniCtaLabel = document.getElementById('packToggleCtaLabel');
    // Add items then chain discount codes to /cart via full-page navigation.
    // Drawer flow requires automatic discounts (not code-based). Code-based
    // BXGY/Basic discounts require the /discount/CODE redirect route, which
    // cannot be driven reliably from fetch(). TODO: migrate BASECOAT_PACK +
    // FINS_PACK to automatic discounts keyed on _pack_id line property, then
    // restore a drawer-first flow.
    fetch(rootPath + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      return res.json().then(function (body) {
        return { ok: res.ok, status: res.status, body: body };
      }).catch(function () {
        return { ok: res.ok, status: res.status, body: null };
      });
    }).then(function (result) {
      if (!result.ok) {
        const serverMsg = result.body && (result.body.message || result.body.description);
        throw { status: result.status, message: serverMsg };
      }
      return result.body;
    }).then(function () {
      const finsCode = DATA.fins_discount_code || '';
      const cartUrl = rootPath + 'cart';

      // Shopify's discount_code cookie stores exactly ONE code at a time.
      // Neither comma-syntax (/discount/A,B) nor nested redirect chains
      // (/discount/A?redirect=/discount/B) work: comma is stored literally
      // (no match), nested redirects overwrite the previous cookie.
      // Single-code strategy: FINS_PACK BXGY covers ailerons + basecoat
      // (sex-wax-blue-tropic) in a single customerGets pool with quantity=2.
      // Basecoat is always added with ailerons when user selects them.
      let target = cartUrl;
      if (finsCode) {
        target = rootPath + 'discount/' + encodeURIComponent(finsCode) +
                 '?redirect=' + encodeURIComponent(cartUrl);
      }
      window.location.href = target;
    }).catch(function (err) {
      console.error('[pack-builder] add to cart failed', err);
      const serverMsg = (err && err.message) || '';
      // "Maximum quantity already in cart" (stock-1 housse re-add, …):
      // dedicated guidance + cart link, since the cart may already hold a pack.
      const alreadyMax = /maxim|already in your cart|déjà dans votre panier|deja|ya en tu carrito/i.test(serverMsg);
      const fallback = (cta.dataset && cta.dataset.labelError) || 'Error, please retry';
      if (alreadyMax) {
        resetCtaToError(errText('err_max_in_cart', fallback), true);
      } else {
        resetCtaToError(serverMsg || fallback, true);
      }
    });
  }

  // ------------------------------------------------------------------
  // Event bindings
  // ------------------------------------------------------------------
  function bindEvents() {
    // Board length / size variant chips (sidebar)
    root.addEventListener('click', function (e) {
      const chip = e.target.closest('.board-variant-chip');
      if (!chip || !root.contains(chip)) return;
      if (chip.disabled) return;
      const handle = state.boardHandle;
      const board = DATA.boards[handle];
      if (!board) return;
      e.preventDefault();
      const id = chip.dataset.variantId;
      const v = (board.variants || []).find(function (x) {
        return String(x.id) === String(id);
      });
      if (!v || !v.available) return;

      const sb = qs('#packSidebar');
      if (sb) {
        sb.dataset.boardPrice = String(v.price_cents);
        sb.dataset.boardVariantId = String(v.id);
      }

      const boardPriceEl = qs('.pack-selected-board__price');
      if (boardPriceEl) {
        boardPriceEl.textContent = v.price_formatted || formatMoney(v.price_cents);
      }

      const wrap = qs('.pack-selected-board__image');
      if (wrap && v.image_url) {
        let img = qs('img', wrap);
        if (!img) {
          img = document.createElement('img');
          img.loading = 'lazy';
          clearChildren(wrap);
          wrap.appendChild(img);
        }
        img.src = v.image_url;
        img.alt = (board.title || '') + ' \u2014 ' + (v.label || v.title || '');
      }

      qsa('.board-variant-chip', root).forEach(function (c) {
        const on = c === chip;
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      updateSidebar();
    });

    function selectPadSwatch(sw, e) {
      if (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
      if (!sw || sw.dataset.available === 'false') return;
      const card = sw.closest('.accessory-card');
      if (!card) return;
      qsa('.pad-variant-swatch', card).forEach(function (s) {
        s.classList.remove('is-active');
        s.setAttribute('aria-pressed', 'false');
      });
      sw.classList.add('is-active');
      sw.setAttribute('aria-pressed', 'true');
      card.dataset.variantId = sw.dataset.variantId;
      card.dataset.price = sw.dataset.price;
      const padBase = card.dataset.padBaseName || card.dataset.name || 'Pad';
      card.dataset.padBaseName = padBase;
      card.dataset.name = padBase + ' -- ' + (sw.dataset.title || '');
      const priceLabel = card.querySelector('.accessory-card__price');
      if (priceLabel) priceLabel.textContent = formatMoney(parseInt(sw.dataset.price, 10) || 0);
      const imgEl = card.querySelector('.accessory-card__image img');
      const newImg = sw.dataset.image;
      if (imgEl && newImg) {
        imgEl.src = newImg;
        imgEl.removeAttribute('srcset');
        imgEl.alt = padBase + ' -- ' + (sw.dataset.title || '');
      }
      const board = DATA.boards[state.boardHandle];
      const product = board && (board[MF_KEYS.pad] || []).find(function (item) { return item.handle === card.dataset.handle; });
      const variant = product && (product.variants || []).find(function (item) { return String(item.id) === String(sw.dataset.variantId); });
      card.dataset.maxQty = String(maxStockFor(variant));
      setCardQty(card, cardQty(card));
      if (card.classList.contains('selected')) {
        readInitialSelection('pad');
        updateSidebar();
      }
    }

    // Pad variant swatches -- intercept before card toggle and support keyboard.
    root.addEventListener('click', function (e) {
      const sw = e.target.closest('.pad-variant-swatch');
      if (!sw) return;
      selectPadSwatch(sw, e);
    }, true);
    root.addEventListener('keydown', function (e) {
      const sw = e.target.closest('.pad-variant-swatch');
      if (!sw || (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar')) return;
      selectPadSwatch(sw, e);
    }, true);

    // Capture stepper events before the card toggle, including keyboard activation.
    function stepAccessory(btn, e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      const card = btn.closest('.accessory-card');
      if (!card || CATEGORIES.indexOf(card.dataset.category) === -1) return;
      const qty = btn.hasAttribute('data-accessory-inc') ? Math.min(cardMaxQty(card), cardQty(card) + 1) : cardQty(card) - 1;
      if (qty < 1) {
        card.classList.remove('selected');
        setCardPressed(card, false);
        showQtyStepper(card, false);
        setCardQty(card, 1);
      } else {
        card.classList.add('selected');
        setCardPressed(card, true);
        showQtyStepper(card, true);
        setCardQty(card, qty);
      }
      readInitialSelection(card.dataset.category);
      updateSidebar();
    }

    root.addEventListener('click', function (e) {
      const btn = e.target.closest('.accessory-qty-btn');
      if (!btn) return;
      stepAccessory(btn, e);
    }, true);
    root.addEventListener('keydown', function (e) {
      const btn = e.target.closest('.accessory-qty-btn');
      if (!btn || (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar')) return;
      stepAccessory(btn, e);
    }, true);

    root.addEventListener('click', function (e) {
      const boardCard = e.target.closest('a[data-board-filter-card]');
      if (boardCard && boardCard.dataset.boardAvailable !== 'true') {
        e.preventDefault();
        return;
      }
      const card = e.target.closest('.accessory-card');
      if (card && root.contains(card)) {
        e.preventDefault();
        selectCard(card);
      }
    });

    const cta = document.getElementById('packAddToCart');
    if (cta) cta.addEventListener('click', addPackToCart);

    // Mobile mini-CTA in toggle bar -- 1-tap add-to-cart without expanding drawer
    const miniCta = document.getElementById('packToggleCta');
    if (miniCta) {
      miniCta.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        addPackToCart();
      });
    }

    const toggle = document.getElementById('packSidebarToggle');
    if (toggle) {
      toggle.addEventListener('click', function () {
        const sb = document.getElementById('packSidebar');
        if (!sb) return;
        const isCollapsed = sb.classList.toggle('collapsed');
        toggle.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
      });
    }
  }

  // ------------------------------------------------------------------
  // Init
  // ------------------------------------------------------------------
  function init() {
    const urlBoard = resolveUrlBoard(getUrlBoard());
    const step0 = document.getElementById('packStep0');
    const step1 = document.getElementById('packStep1');

    if (urlBoard && DATA.boards[urlBoard]) {
      if (step0) step0.hidden = true;
      if (step1) step1.hidden = false;
      swapToBoard(urlBoard);
    } else if (urlBoard && !DATA.boards[urlBoard]) {
      // Unknown ?board= handle -- fall back to Step 0 picker rather than a
      // confusing empty Step 1 with the SSR default.
      console.warn('[pack-builder] unknown board handle in URL, showing picker:', urlBoard);
      if (step1) step1.hidden = true;
      if (step0) step0.hidden = false;
    } else if (state.boardHandle && DATA.boards[state.boardHandle]) {
      if (step0) step0.hidden = true;
      if (step1) step1.hidden = false;
      swapToBoard(state.boardHandle);
    } else {
      if (step1) step1.hidden = true;
      if (step0) step0.hidden = false;
    }

    syncHeadings();
    setupBoardFilters();
    setupStepNavigation();
    bindEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
